import { IonPage, useIonRouter, useIonViewDidEnter, useIonViewDidLeave, useIonViewWillEnter } from '@ionic/react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { setActiveTab } from 'src/redux/tabBar/reducer';
import { setIsActive } from 'src/redux/tabBar/reducer';
import { useDispatch, useSelector } from 'react-redux';
import { Tabs } from 'src/redux/tabBar/reducer';
import { PrivateRoutes, PublicRoutes } from 'src/routes/navigation';
import { useSwipeBackGesture } from 'src/shared/hooks/useSwipeBackGesture';
import { TranslatedSessionDetailWidget } from 'src/translated-components';
import { userSelector } from 'src/redux/user/selector';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { Session, Specialist } from '@mindly/ui-components';
import { getConsultationByID } from 'src/api/consultation';
import { ImpactStyle } from '@capacitor/haptics';
import { ContractWithSpecialist } from 'src/components/ContractWithSpecialist';
import { ContractWithSpecialistRef } from 'src/components/ContractWithSpecialist/ContractWithSpecialist';
import SessionStart, { SessionStartRef } from 'src/components/SessionStart/SessionStart';
import { getUsersPsychologistsListThunk } from 'src/redux/usersPsychologists/action';
import { usersPsychologistsSelector } from 'src/redux/usersPsychologists/selector';
import { useChat } from 'src/shared/hooks/useChat';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from 'src/shared';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';

interface SessionDetailProps
  extends RouteComponentProps<{
    id: string;
  }> {}

const SessionDetail: React.FC<SessionDetailProps> = ({ match }) => {
  const { i18n } = useTranslation(['ui']);
  const pageRef = useRef();
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  const history = useHistory();
  const [session, setSession] = useState<Session>();
  const [isLoading, setIsLoading] = useState(true);
  const contractRef = useRef<ContractWithSpecialistRef>(null);
  const sessionStartRef = useRef<SessionStartRef>(null);
  const usersPsychologistList = useSelector(usersPsychologistsSelector);
  const navigation = useIonRouter();
  const { handleChatClick: handleChatClickFromContext } = useChat();
  const { trackPredefinedEvent } = useAnalytics();
  const specialist = useMemo(
    () => usersPsychologistList?.find((s) => s.id === session?.psychologist_id),
    [session?.psychologist_id, usersPsychologistList]
  );

  useSwipeBackGesture(pageRef);

  useIonViewDidLeave(() => {
    setSession(undefined);
    setIsLoading(true);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const sessionData = await getConsultationByID(match.params.id);
      setSession(sessionData as unknown as Session);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [match.params.id]);

  useIonViewWillEnter(() => {
    if (!user?.id) {
      history.push(PublicRoutes.AUTH);
      return;
    }

    dispatch(setIsActive(false));
    dispatch(setActiveTab(Tabs.second));
    fetchData();
    if (!usersPsychologistList) {
      dispatch(getUsersPsychologistsListThunk());
    }
  }, [user?.id, fetchData, usersPsychologistList]);

  useIonViewDidEnter(() => {
    trackPredefinedEvent('SESSION_DETAILS_SCREEN_VIEWED', {
      specialist_id: session?.psychologist_id,
      specialist_name: `${session?.therapist?.first_name} ${session?.therapist?.last_name ?? 'undefined'}`,
    });
  }, [trackPredefinedEvent]);

  const handleBackClick = useCallback(() => {
    history.back();
  }, []);

  const handleSpecialistProfileClick = useCallback(async (data: Session) => {
    isomorficHapticsImpact({ style: ImpactStyle.Light });
    history.push(PrivateRoutes.PSYCHOLOGIST_PROFILE_TAB.replace(':id', data.psychologist_id));
  }, []);

  const handleContractClick = useCallback(() => {
    contractRef.current?.openContract();
  }, []);

  const handleChatClick = useCallback(
    (data: Session) => {
      handleChatClickFromContext(data.psychologist_id);
    },
    [handleChatClickFromContext]
  );

  const handleRescheduleClick = useCallback((data: Session) => {
    history.push(
      PrivateRoutes.BOOKING_SYSTEM.replace(':id', data.psychologist_id).replace(':consultationID?', data.id)
    );
  }, []);

  const handleCancelClick = useCallback((data: Session) => {
    history.push(PrivateRoutes.CANCEL_SESSION_CONFIRM.replace(':sessionID', data.id));
  }, []);

  const handleSessionStart = useCallback((data: Session) => {
    trackPredefinedEvent('SESSION_START_BUTTON_CLICKED', {
      specialist_id: data?.psychologist_id ?? `${`undefined-session-id-${session?.id}`}`,
      specialist_name: `${data?.therapist?.first_name ?? 'undefined'} ${data?.therapist?.last_name ?? 'undefined'}`,
      is_trial: !!data.payment_intent_id,
    });
    sessionStartRef.current?.openSession(data);
  }, []);

  const handlePaymentDetailsClick = useCallback((data: Session) => {
    history.push(PrivateRoutes.SESSION_PAYMENTS.replace(':id', data.id));
  }, []);

  const sessionTherapist = useMemo(() => {
    return session?.therapist ?? (specialist as unknown as Specialist);
  }, [session, specialist]);

  const psychologistAvatar = useMemo(() => {
    return session?.psychologist_avatar ?? specialist?.avatar_link ?? '';
  }, [session, specialist]);

  return (
    <IonPage ref={pageRef}>
      <TranslatedSessionDetailWidget
        isLoading={isLoading || !session || !sessionTherapist}
        session={
          session && sessionTherapist
            ? {
                ...session,
                psychologist_avatar: psychologistAvatar,
                therapist: sessionTherapist,
              }
            : undefined
        }
        onBackClick={handleBackClick}
        onSpecialistProfileClick={handleSpecialistProfileClick}
        onContractClick={handleContractClick}
        onChatClick={handleChatClick}
        onRescheduleClick={handleRescheduleClick}
        onCancelClick={handleCancelClick}
        onSessionStart={handleSessionStart}
        onPaymentDetailsClick={handlePaymentDetailsClick}
        specialist={specialist as unknown as Specialist}
        locale={i18n.language}
      />
      {/* to prevent open second contract modal when user click on specialist profile from sessions list */}
      {!navigation.routeInfo?.pathname?.includes(PrivateRoutes.PSYCHOLOGIST_PROFILE.replace('/:id', '')) && (
        <ContractWithSpecialist ref={contractRef} specialistId={session?.psychologist_id ?? ''} />
      )}
      <SessionStart ref={sessionStartRef} />
    </IonPage>
  );
};

export default SessionDetail;
